// import React, { useContext } from 'react'
import React from 'react'
import styled, { css } from 'styled-components'
import { string, func, bool, array, object } from 'prop-types'
import { stripUnit, rgba } from 'polished'

import { H4, fontWeights, fontSizes } from 'styles/typography'
import * as spacing from 'styles/spacing'
import { boxShadow, borderRadius, square, cover } from 'styles/global'
import { borderColor, greyDark, offWhite, red, white, green } from 'styles/colors'
import { media, mediaDown } from 'styles/media'

import QuantityPicker from 'components/QuantityPicker'
// import { EngravingContext } from 'components/buy/Engraving/EngravingContext'
import EngravingAddon from 'components/buy/EngravingAddon'
import Icon from 'components/Icon'

import CloseSmall from 'icons/close-small.svg'

const Container = styled.div`
  position: relative;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  padding: ${spacing.large};
  background-color: ${white};
  ${boxShadow};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}

  & + & {
    margin-top: ${spacing.medium};
  }
`

const Inner = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;

  ${mediaDown.small`
    flex-wrap: wrap;
  `};
`

const Overlay = styled.div`
  cursor: pointer;
  position: absolute;
  top: -${spacing.large};
  left: -${spacing.large};
  right: -${spacing.large};
  bottom: -${spacing.medium};
  z-index: 10;
`

const Heading = styled(H4)`
  font-weight: ${fontWeights.bold};
  margin-bottom: ${spacing.small};
`

const FigureWrap = styled.div`
  position: relative;
  margin-right: ${spacing.medium};
  width: 48px;
  border-radius: ${borderRadius};
  overflow: hidden;

  ${media.medium`
    width: 72px;
  `};

  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`

const Figure = styled.img`
  object-fit: cover;
  ${cover('absolute')};
`

const Price = styled.span`
  font-weight: ${fontWeights.regular};
  white-space: nowrap;
`

const Description = styled.p`
  font-size: ${fontSizes.small};
`

const Main = styled.main`
  flex: 1;
  padding-right: ${spacing.medium};
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const Action = styled.button`
  display: flex;
  color: ${greyDark};
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.small};
  flex-shrink: 0;

  ${props =>
    props.active &&
    css`
      color: ${green};
    `};
`

const Products = styled.div`
  margin-top: ${spacing.medium};

  ${media.large`
    margin-left: 88px;
  `};
`

export const ProductContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${borderColor};
  border-radius: ${borderRadius};
  padding: ${spacing.small} ${spacing.medium};
  font-size: ${fontSizes.small};
  background-color: ${rgba(offWhite, 0.75)};

  ${media.medium`
    padding: ${stripUnit(spacing.small) * 1.5 + 'px'} ${spacing.medium};
  `};

  & + & {
    margin-top: ${spacing.small};

    ${media.medium`
      margin-top: ${stripUnit(spacing.small) * 1.5 + 'px'};
    `};
  }
`

export const ProductFigureWrap = styled.div`
  position: relative;
  border-radius: 50%;
  margin-right: ${stripUnit(spacing.small) * 1.5 + 'px'};
  overflow: hidden;
  ${square('24px')};

  ${mediaDown.medium`
    margin-left: -${spacing.tiny};
  `};
`

export const ProductFigure = styled.img`
  object-fit: cover;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);

  ${props =>
    props.isSkin &&
    css`
      transform: translate3d(-50%, -20%, 0) scale(9);
    `};
`

export const ProductName = styled.div`
  flex: 1;
  text-align: left;
  padding-right: ${spacing.medium};
  text-transform: capitalize;
`

const ProductRemove = styled.button`
  display: flex;
  color: ${greyDark};
  font-weight: ${fontWeights.medium};
  flex-shrink: 0;
`

const Label = styled.span`
  font-size: ${fontSizes.small};
  font-weight: bold;
  color: ${greyDark};
`

const QuantityWrap = styled.div`
  align-self: center;

  ${mediaDown.small`
    flex-basis: 100%; /* Force onto new row */
    padding-left: 64px;
    margin-top: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};
`

const Remove = styled.button`
  display: flex;
  color: ${green};
  margin-left: ${spacing.tiny};

  ${media.large`
    margin-left: ${spacing.small};
  `};
`

const Tag = styled.div`
  position: absolute;
  top: -${spacing.small};
  left: -${spacing.small};
  display: inline-block;
  background-color: ${red};
  color: ${white};
  text-transform: uppercase;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.tiny};
  padding: 0.2em 6px;
  text-align: center;
  white-space: nowrap;
  border-radius: ${borderRadius};
`

const Addon = ({
  figure,
  heading,
  price,
  description,
  onAdd,
  onRemove,
  active,
  products,
  isEngraving,
  tag,
  isAvailableForSale,
  withQuantityPicker,
  handleAccessoryQuantityChange,
  product,
  handleSkinRemove
}) => {
  // const { engraving } = useContext(EngravingContext)
  const engraving = {}

  return (
    <Container disabled={!isAvailableForSale}>
      {tag && <Tag>{tag}</Tag>}
      <Inner>
        {!active && isAvailableForSale && !withQuantityPicker && (
          <Overlay
            onClick={() => {
              onAdd()
            }}
          />
        )}
        <FigureWrap>
          <Figure src={figure} />
        </FigureWrap>
        <Main>
          <Heading>
            {heading}
            {price && <Price>&nbsp;+ {price}</Price>}
          </Heading>
          {description && <Description>{description}</Description>}
        </Main>
        {isAvailableForSale ? (
          <>
            {withQuantityPicker ? (
              <QuantityWrap>
                <QuantityPicker
                  onChange={handleAccessoryQuantityChange}
                  value={product.variants[0].quantity}
                  product={product}
                />
              </QuantityWrap>
            ) : (
              <ActionsWrapper>
                <Action
                  role="button"
                  onClick={() => {
                    onAdd()
                  }}
                  disabled={active}
                  active={active}
                >
                  {!active ? '+ Add' : !isEngraving ? 'Added' : 'Engraved'}
                </Action>
                {active && !isEngraving && (
                  <Remove
                    onClick={() => {
                      onRemove()
                    }}
                  >
                    <Icon icon={<CloseSmall />} />
                  </Remove>
                )}
              </ActionsWrapper>
            )}
          </>
        ) : (
          <Label>Unavailable</Label>
        )}
      </Inner>
      {products?.length > 0 && heading === 'ePod skins' && handleSkinRemove && (
        <Products>
          {products.map((p, idx) => (
            <Product
              key={idx}
              name={p.title}
              figure={p.variants[0].image.originalSrc}
              quantity={p.variants[0].quantity}
              isSkin
              isEngraving={isEngraving}
              onRemove={() => {
                handleSkinRemove(p.id)
              }}
            />
          ))}
        </Products>
      )}
      {isEngraving && engraving.proceeded && (
        <Products>
          <EngravingAddon />
        </Products>
      )}
    </Container>
  )
}

Addon.propTypes = {
  figure: string.isRequired,
  heading: string.isRequired,
  price: string,
  productType: string,
  description: string,
  tag: string,
  onAdd: func,
  onRemove: func,
  active: bool,
  products: array,
  isEngraving: bool,
  isAvailableForSale: bool,
  withQuantityPicker: bool,
  product: object,
  handleAccessoryQuantityChange: func,
  handleSkinRemove: func
}

export const Product = ({ figure, name, quantity, isSkin, onRemove }) => {
  return (
    <ProductContainer>
      <ProductFigureWrap>
        <ProductFigure src={figure} alt={name} isSkin={isSkin} />
      </ProductFigureWrap>
      <ProductName>
        {name} × {quantity}
      </ProductName>
      <ProductRemove onClick={onRemove}>Remove</ProductRemove>
    </ProductContainer>
  )
}

Product.propTypes = {
  figure: string.isRequired,
  name: string.isRequired,
  quantity: string.isRequired,
  isSkin: bool,
  onRemove: func
}

export default Addon
